/**
 * Destination countries a foreign employment agency actually places workers in
 * — not a full ISO list, which would bury Kuwait under 190 irrelevant options
 * in every dropdown. Codes are ISO 3166-1 alpha-2 so the list can be widened
 * without a migration.
 */

export interface Country {
  code: string
  name: string
  region: CountryRegion
}

export const COUNTRY_REGIONS = ['middle_east', 'east_asia', 'europe', 'other'] as const
export type CountryRegion = (typeof COUNTRY_REGIONS)[number]

export const COUNTRY_REGION_LABELS: Record<CountryRegion, string> = {
  middle_east: 'Middle East',
  east_asia: 'Asia & Pacific',
  europe: 'Europe',
  other: 'Other',
}

export const COUNTRIES: readonly Country[] = [
  // Middle East — the bulk of placements
  { code: 'SA', name: 'Saudi Arabia', region: 'middle_east' },
  { code: 'AE', name: 'United Arab Emirates', region: 'middle_east' },
  { code: 'QA', name: 'Qatar', region: 'middle_east' },
  { code: 'KW', name: 'Kuwait', region: 'middle_east' },
  { code: 'OM', name: 'Oman', region: 'middle_east' },
  { code: 'BH', name: 'Bahrain', region: 'middle_east' },
  { code: 'JO', name: 'Jordan', region: 'middle_east' },
  { code: 'LB', name: 'Lebanon', region: 'middle_east' },
  { code: 'IL', name: 'Israel', region: 'middle_east' },
  { code: 'TR', name: 'Türkiye', region: 'middle_east' },

  // Asia & Pacific
  { code: 'KR', name: 'South Korea', region: 'east_asia' },
  { code: 'JP', name: 'Japan', region: 'east_asia' },
  { code: 'SG', name: 'Singapore', region: 'east_asia' },
  { code: 'MY', name: 'Malaysia', region: 'east_asia' },
  { code: 'HK', name: 'Hong Kong', region: 'east_asia' },
  { code: 'MV', name: 'Maldives', region: 'east_asia' },
  { code: 'AU', name: 'Australia', region: 'east_asia' },
  { code: 'NZ', name: 'New Zealand', region: 'east_asia' },

  // Europe — growing destination for factory/construction work
  { code: 'RO', name: 'Romania', region: 'europe' },
  { code: 'PL', name: 'Poland', region: 'europe' },
  { code: 'HR', name: 'Croatia', region: 'europe' },
  { code: 'HU', name: 'Hungary', region: 'europe' },
  { code: 'CZ', name: 'Czechia', region: 'europe' },
  { code: 'SK', name: 'Slovakia', region: 'europe' },
  { code: 'LT', name: 'Lithuania', region: 'europe' },
  { code: 'RS', name: 'Serbia', region: 'europe' },
  { code: 'CY', name: 'Cyprus', region: 'europe' },
  { code: 'MT', name: 'Malta', region: 'europe' },
  { code: 'IT', name: 'Italy', region: 'europe' },
  { code: 'PT', name: 'Portugal', region: 'europe' },
  { code: 'GB', name: 'United Kingdom', region: 'europe' },

  { code: 'SC', name: 'Seychelles', region: 'other' },
  { code: 'MU', name: 'Mauritius', region: 'other' },
  { code: 'CA', name: 'Canada', region: 'other' },
]

export const COUNTRY_CODES = COUNTRIES.map((c) => c.code)

const COUNTRY_BY_CODE = new Map(COUNTRIES.map((c) => [c.code, c]))

export function countryName(code: string): string {
  return COUNTRY_BY_CODE.get(code)?.name ?? code
}

export function isCountryCode(code: string): boolean {
  return COUNTRY_BY_CODE.has(code)
}
